import { useRef, useState } from 'react';

import {
  FILE_REJECTION_MESSAGES_AR,
  formatBytes,
  rejectFile,
} from '../features/documents/application/documentChecklist';
import { Alert, Badge, Button, Card } from '../shared/components';

import authStyles from './ScreenLayout.module.css';
import styles from './DependentsPage.module.css';

/**
 * The document checklist, with an upload control per requirement.
 *
 * Each row is one thing the employee has to provide, named after the person it
 * proves. A single "upload documents" box would leave them guessing which of
 * their four children still needs a certificate.
 */

export interface DocumentRequirementRow {
  readonly key: string;
  readonly labelAr: string;
  readonly forNameAr: string | null;
  readonly satisfied: boolean;
  readonly fileNameAr: string | null;
  readonly sizeBytes: number | null;
}

export interface DocumentsPageProps {
  readonly requirements: readonly DocumentRequirementRow[];
  readonly uploadingKey: string | null;
  readonly errorAr: string | null;
  readonly disabled: boolean;
  readonly onUpload: (key: string, file: File) => void;
  /**
   * Opens the stored file for one viewing.
   *
   * The link is fetched at this moment rather than held anywhere: a Firebase
   * download URL is a bearer token, and a stored one outlives the permission
   * that produced it.
   */
  readonly onView: (key: string) => void;
  /** The row whose link is being fetched, if any. */
  readonly viewingKey: string | null;
}

export function DocumentsPage({
  requirements,
  uploadingKey,
  errorAr,
  disabled,
  onUpload,
  onView,
  viewingKey,
}: DocumentsPageProps) {
  const [rejectionAr, setRejectionAr] = useState<string | null>(null);
  const outstanding = requirements.filter((entry) => !entry.satisfied);

  return (
    <div className={authStyles.page}>
      <h1 className="visually-hidden">الوثائق</h1>

      <Card title="الوثائق المطلوبة" description="صوّر المستند أو ارفع ملف PDF">
        {errorAr ? <Alert severity="error">{errorAr}</Alert> : null}
        {rejectionAr ? <Alert severity="warning">{rejectionAr}</Alert> : null}

        {disabled ? <Alert severity="warning">فترة التقديم مغلقة حاليًا.</Alert> : null}

        {outstanding.length === 0 ? (
          <Alert severity="success">اكتملت جميع الوثائق المطلوبة.</Alert>
        ) : (
          <Alert severity="warning" title="ناقص">
            {`بقي ${String(outstanding.length)} من المستندات.`}
          </Alert>
        )}

        <div className={styles.list}>
          {requirements.map((requirement) => (
            <DocumentRow
              key={requirement.key}
              requirement={requirement}
              uploading={uploadingKey === requirement.key}
              disabled={disabled}
              onReject={setRejectionAr}
              onUpload={onUpload}
              onView={onView}
              viewing={viewingKey === requirement.key}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

function DocumentRow({
  requirement,
  uploading,
  disabled,
  onReject,
  onUpload,
  onView,
  viewing,
}: {
  readonly requirement: DocumentRequirementRow;
  readonly uploading: boolean;
  readonly disabled: boolean;
  readonly onReject: (message: string | null) => void;
  readonly onUpload: (key: string, file: File) => void;
  readonly onView: (key: string) => void;
  readonly viewing: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Checked before the upload starts, so nobody watches a 40 MB photo climb
    // over a phone connection only to be refused at the end. The storage rules
    // check again, and that check is the boundary.
    const rejection = rejectFile(file);
    if (rejection) {
      onReject(FILE_REJECTION_MESSAGES_AR[rejection]);
      // Clearing lets the same file be chosen again after it is fixed;
      // otherwise the change event never fires a second time.
      event.target.value = '';
      return;
    }

    onReject(null);
    onUpload(requirement.key, file);
    event.target.value = '';
  };

  return (
    <div className={styles.entry}>
      <div className={styles.entryDetails}>
        <span className={styles.entryName}>
          {requirement.forNameAr
            ? `${requirement.labelAr} — ${requirement.forNameAr}`
            : requirement.labelAr}
        </span>
        {requirement.satisfied && requirement.fileNameAr ? (
          <span className={styles.entryMeta}>
            {requirement.sizeBytes === null
              ? requirement.fileNameAr
              : `${requirement.fileNameAr} · ${formatBytes(requirement.sizeBytes)}`}
          </span>
        ) : (
          <span className={styles.entryMeta}>لم يُرفع بعد</span>
        )}
      </div>

      <div className={styles.entryActions}>
        {requirement.satisfied ? (
          <Badge tone="success" withDot>
            مرفوع
          </Badge>
        ) : (
          <Badge tone="warning" withDot>
            مطلوب
          </Badge>
        )}
      </div>

      <div className={styles.entryActions}>
        {/* Seeing what was actually sent. A name and a size do not tell anyone
            whether they attached the right page. */}
        {requirement.satisfied ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={viewing}
            onClick={() => {
              onView(requirement.key);
            }}
            aria-label={`معاينة ${requirement.labelAr}${
              requirement.forNameAr ? ` لـ ${requirement.forNameAr}` : ''
            }`}
          >
            معاينة
          </Button>
        ) : null}

        {/* `capture` is deliberately absent: it would force the camera and stop
            someone attaching a scan they already have. `accept` filters the
            picker without being relied on for validation. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
          className="visually-hidden"
          onChange={handleFile}
          disabled={disabled || uploading}
          aria-label={`ارفع ${requirement.labelAr}${
            requirement.forNameAr ? ` لـ ${requirement.forNameAr}` : ''
          }`}
        />
        <Button
          variant={requirement.satisfied ? 'secondary' : 'primary'}
          size="sm"
          loading={uploading}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          {requirement.satisfied ? 'استبدال' : 'رفع'}
        </Button>
      </div>
    </div>
  );
}
